<template>
  <div class="experience-editor">
    <header class="experience-editor__header">
      <div class="experience-editor__hero">
        <div class="experience-editor__title-row">
          <h1>Experience workspace</h1>
        </div>
        <p class="experience-editor__intro">
          Dedicated authoring surface for reusable experiences. This editor reuses the
          existing quiz and survey editing components without the campaign-builder baggage.
        </p>
        <p class="experience-editor__workflow">
          {{ workflowMessage }}
        </p>
      </div>
      <div class="experience-editor__actions">
        <router-link class="experience-editor__ghost" to="/dashboard/experiences">
          Back to library
        </router-link>
        <router-link
          class="experience-editor__ghost"
          :to="{ path: '/', query: { experienceDraft: localConfig.experienceDefinitionLocalKey, prefill: 'true' } }"
        >
          Open in builder
        </router-link>
      </div>
    </header>

    <section class="experience-editor__layout">
      <div class="experience-editor__sidebar">
        <div
          v-if="editorMessage"
          class="experience-editor__banner"
          :class="`experience-editor__banner--${editorMessage.kind}`"
        >
          {{ editorMessage.text }}
        </div>

        <article class="experience-editor__family">
          <header class="experience-editor__family-header">
            <div class="experience-editor__family-copy">
              <h3>{{ experienceName || title }}</h3>
              <p class="experience-editor__family-mode">
                {{ localConfig.experienceMode === 'survey' ? 'Survey' : 'Quiz' }}
              </p>
              <p class="experience-editor__family-summary">
                {{ localConfig.experienceTemplateFamilyId || localConfig.quizTemplateId || 'General template' }} •
                v{{ existingRecord?.versionNumber || 1 }} •
                {{ versionHistory.length }} version{{ versionHistory.length === 1 ? '' : 's' }} •
                {{ saveFeedback }}
              </p>
            </div>
            <div class="experience-editor__family-badges">
              <span class="experience-editor__family-status" :class="`experience-editor__family-status--${localConfig.experienceDefinitionStatus}`">
                {{ localConfig.experienceDefinitionStatus }}
              </span>
              <span class="experience-card__version-state experience-card__version-state--latest">
                v{{ existingRecord?.versionNumber || 1 }} current
              </span>
              <span class="experience-library__count">{{ versionHistory.length || 1 }}</span>
            </div>
          </header>

          <div class="experience-editor__family-blocks">

        <!-- 1. Compact meta line: version · template · shape · save state -->
        <div class="experience-editor__panel experience-editor__panel--compact-meta">
          <span class="experience-editor__compact-meta-line">
            v{{ existingRecord?.versionNumber || 1 }}
            · {{ localConfig.experienceTemplateFamilyId || localConfig.quizTemplateId || 'General' }}
            · {{ roundsLabel }} · {{ stagesLabel }} · {{ questionsLabel }}
          </span>
          <span class="experience-editor__compact-meta-save">{{ saveFeedback }}</span>
        </div>

        <!-- 2. Version history -->
        <div v-if="versionHistory.length > 0" class="experience-editor__panel">
          <div class="experience-editor__panel-head">
            <h4>Version history</h4>
            <span class="experience-library__count">{{ versionHistory.length }}</span>
          </div>
          <div class="experience-editor__history">
            <router-link
              v-for="record in versionHistory"
              :key="record.localKey"
              class="experience-editor__history-item"
              :class="{ 'experience-editor__history-item--active': record.localKey === localConfig.experienceDefinitionLocalKey }"
              :to="{ path: `/dashboard/experiences/${record.localKey}` }"
            >
              <div class="experience-editor__history-row">
                <span class="experience-editor__history-version">v{{ record.versionNumber || 1 }}</span>
                <div class="experience-editor__history-badges">
                  <span v-if="isLatestPublishedRecord(record)" class="experience-editor__history-badge experience-editor__history-badge--latest">
                    latest
                  </span>
                  <span class="experience-editor__history-status" :class="`experience-editor__history-status--${record.status}`">
                    {{ record.status }}
                  </span>
                </div>
              </div>
              <span class="experience-editor__history-name">{{ record.name }}</span>
              <span class="experience-editor__history-date">
                {{ formatDate(record.publishedAt || record.updatedAt) }}
              </span>
              <span class="experience-editor__history-usage">
                {{ historyUsageLabel(record) }}
              </span>
            </router-link>
          </div>
        </div>
          </div>
        </article>
      </div>

      <div class="experience-editor__main">
        <!-- Banner: edits don't propagate to attached campaigns -->
        <div
          v-if="attachedCampaigns.length > 0"
          class="experience-editor__banner experience-editor__banner--info"
        >
          <strong>{{ attachedCampaigns.length }} campaign{{ attachedCampaigns.length === 1 ? '' : 's' }} use a snapshot of this template.</strong>
          Edits here apply to <em>new</em> attachments only — existing campaigns keep their current pack until you re-attach.
        </div>

        <section v-if="isPublishedRecord" class="experience-editor__readonly">
          <h2>Published version</h2>
          <p>
            Published experiences are read-only here. Create a draft copy to continue editing without
            mutating the published version.
          </p>
          <div class="experience-editor__workflow-actions experience-editor__workflow-actions--readonly">
            <button
              type="button"
              class="experience-editor__save-cta"
              :disabled="isSaving"
              @click="editAsDraft"
            >
              {{ pendingAction === 'fork' ? 'Creating draft...' : 'Edit as draft' }}
            </button>
            <button
              type="button"
              class="experience-editor__archive-btn"
              :disabled="isSaving"
              @click="archiveExperience"
            >
              Archive this experience
            </button>
          </div>
        </section>

        <!-- Main workspace: QuizSection (theme + modal triggers) + inline rounds canvas -->
        <section v-if="!isPublishedRecord" class="experience-editor__workspace-panel">
          <div class="experience-editor__workspace-head">
            <div>
              <h2>Experience configuration</h2>
            </div>
            <span class="experience-editor__workspace-badge">
              {{ roundsLabel }} · {{ stagesLabel }} · {{ questionsLabel }}
            </span>
          </div>

          <div class="experience-editor__configuration-basics">
            <div v-if="isNewExperience" class="experience-editor__meta-card">
              <span class="experience-editor__meta-label">Experience type</span>
              <div class="experience-editor__type-picker">
                <button
                  v-for="mode in newExperienceModes"
                  :key="mode.id"
                  type="button"
                  class="experience-editor__type-pill"
                  :class="{ 'experience-editor__type-pill--active': localConfig.experienceMode === mode.id }"
                  :disabled="isSaving"
                  @click="selectNewExperienceMode(mode.id)"
                >
                  <span class="experience-editor__type-pill-label">{{ mode.label }}</span>
                  <span class="experience-editor__type-pill-desc">{{ mode.description }}</span>
                </button>
              </div>
            </div>
            <div class="experience-editor__meta-card">
              <span class="experience-editor__meta-label">Name</span>
              <input
                v-model="experienceName"
                class="experience-editor__input"
                type="text"
                maxlength="120"
                placeholder="Quiz Experience"
                :disabled="isPublishedRecord"
              />
            </div>
            <div class="experience-editor__meta-card">
              <span class="experience-editor__meta-label">Status</span>
              <div class="experience-editor__status-toggle">
                <button
                  type="button"
                  class="experience-editor__status-state"
                  :class="{ 'experience-editor__status-state--active': localConfig.experienceDefinitionStatus === 'draft' }"
                  :disabled="isPublishedRecord || isSaving"
                  @click="() => saveExperience('draft')"
                >
                  Draft
                </button>
                <button
                  type="button"
                  class="experience-editor__status-state"
                  :class="{ 'experience-editor__status-state--active': localConfig.experienceDefinitionStatus === 'published' }"
                  :disabled="isPublishedRecord || isSaving"
                  @click="() => saveExperience('published')"
                >
                  Published
                </button>
              </div>
            </div>
          </div>

          <QuizSection
            ref="quizSectionRef"
            :model-value="localConfig"
            :campaign-schedule="campaignSchedule"
            :experience-type="lockedExperienceType"
            :show-template-picker="true"
            :show-schedule-editor="false"
            summary-context="experience"
            @update:model-value="applyLocalConfigUpdate"
          />

          <!-- F3: Cycling schedule — section within Experience Configuration panel -->
          <div class="experience-editor__periodic-panel experience-editor__periodic-panel--inline">
            <div class="experience-editor__periodic-head">
              <h3 class="experience-editor__periodic-title">Cycling schedule <span class="experience-editor__periodic-title-optional">(optional)</span></h3>
              <label class="experience-editor__periodic-toggle">
                <input
                  type="checkbox"
                  :checked="periodicEnabled"
                  @change="togglePeriodic"
                />
                {{ periodicEnabled ? 'Enabled' : 'Disabled' }}
              </label>
            </div>

            <!-- F3b: schedule-mode toggle. Uniform = legacy weekly/daily/hourly cadence;
                 Explicit rounds = per-round start/end windows + optional maxAttempts cap. -->
            <div v-if="periodicEnabled && localConfig.periodicSchedule" class="experience-editor__periodic-mode-toggle">
              <span class="experience-editor__periodic-mode-label">Schedule mode</span>
              <div class="experience-editor__periodic-mode-options">
                <label class="experience-editor__periodic-mode-option" :class="{ 'is-active': periodicMode === 'uniform-interval' }">
                  <input
                    type="radio"
                    name="periodic-mode"
                    value="uniform-interval"
                    :checked="periodicMode === 'uniform-interval'"
                    @change="setPeriodicMode('uniform-interval')"
                  />
                  Uniform interval
                </label>
                <label class="experience-editor__periodic-mode-option" :class="{ 'is-active': periodicMode === 'explicit-rounds' }">
                  <input
                    type="radio"
                    name="periodic-mode"
                    value="explicit-rounds"
                    :checked="periodicMode === 'explicit-rounds'"
                    @change="setPeriodicMode('explicit-rounds')"
                  />
                  Explicit rounds (custom start/end per round)
                </label>
              </div>
            </div>

            <!-- F8: Schedule preview shown whether cycling enabled or not -->
            <div class="experience-editor__periodic-preview experience-editor__periodic-preview--prominent experience-editor__periodic-preview--spaced">
              <span class="experience-editor__periodic-preview-title">Schedule preview</span>
              <div v-if="periodicPreviewLines.length > 0" class="experience-editor__periodic-preview-lines">
                <span
                  v-for="(line, idx) in periodicPreviewLines"
                  :key="idx"
                  class="experience-editor__periodic-preview-line"
                >{{ line }}</span>
              </div>
              <p v-else class="experience-editor__periodic-preview-empty">
                No cycling schedule — single-run experience.
              </p>
            </div>

            <div v-if="periodicEnabled && localConfig.periodicSchedule" class="experience-editor__periodic-fields experience-editor__periodic-fields--spaced">
              <!-- Uniform-interval anchor + interval (hidden in explicit-rounds mode) -->
              <template v-if="periodicMode === 'uniform-interval'">
                <div class="experience-editor__periodic-row">
                  <label class="experience-editor__periodic-label" for="periodic-start-date-inline">Start date &amp; time (UTC)</label>
                  <input
                    id="periodic-start-date-inline"
                    type="datetime-local"
                    class="experience-editor__periodic-input"
                    :value="periodicStartLocal"
                    @change="updatePeriodicStart(($event.target as HTMLInputElement).value)"
                  />
                </div>

                <div class="experience-editor__periodic-row">
                  <span class="experience-editor__periodic-label">Interval</span>
                  <div class="experience-editor__periodic-interval-group">
                    <input
                      type="number"
                      min="1"
                      class="experience-editor__periodic-input"
                      :value="periodicIntervalValue"
                      placeholder="1"
                      @input="updatePeriodicInterval(Number(($event.target as HTMLInputElement).value), periodicIntervalUnit)"
                    />
                    <select
                      class="experience-editor__periodic-select"
                      :value="periodicIntervalUnit"
                      @change="updatePeriodicInterval(periodicIntervalValue, ($event.target as HTMLSelectElement).value as PeriodicIntervalUnit)"
                    >
                      <option value="hour">Hour (3 600 s)</option>
                      <option value="day">Day (86 400 s)</option>
                      <option value="week">Week (604 800 s)</option>
                      <option value="month">Month (2 592 000 s)</option>
                    </select>
                  </div>
                </div>
              </template>

              <!-- Explicit-rounds: brief hint pointing operator to the per-round inputs below -->
              <div v-else class="experience-editor__periodic-row experience-editor__periodic-explicit-hint">
                <span class="experience-editor__periodic-explicit-hint-text">
                  Set each round's start time, end time, and per-user max attempts inside the round cards below.
                </span>
              </div>

              <!-- Cycle (uniform-interval only) + kill-switch (both modes) -->
              <div class="experience-editor__periodic-row experience-editor__periodic-row--checkboxes">
                <label v-if="periodicMode === 'uniform-interval'" class="experience-editor__periodic-checkbox-row">
                  <input
                    type="checkbox"
                    :checked="localConfig.periodicSchedule.cycle"
                    @change="updatePeriodicField('cycle', ($event.target as HTMLInputElement).checked)"
                  />
                  Loop back to round 1 after the last round
                </label>
                <label class="experience-editor__periodic-checkbox-row">
                  <input
                    type="checkbox"
                    :checked="localConfig.periodicSchedule.killSwitch"
                    @change="updatePeriodicField('killSwitch', ($event.target as HTMLInputElement).checked)"
                  />
                  Kill switch (emergency stop — users see the completion message)
                </label>
              </div>

              <!-- Completion message -->
              <div class="experience-editor__periodic-row">
                <label class="experience-editor__periodic-label" for="periodic-completion-msg-inline">Completion message</label>
                <textarea
                  id="periodic-completion-msg-inline"
                  class="experience-editor__periodic-textarea"
                  :value="localConfig.periodicSchedule.completionMessage ?? `Come back next ${localConfig.periodicSchedule.intervalNoun}.`"
                  @input="updatePeriodicField('completionMessage', ($event.target as HTMLTextAreaElement).value)"
                />
              </div>

              <!-- Validation errors -->
              <div v-if="periodicValidationErrors.length > 0" class="experience-editor__periodic-validation">
                <span
                  v-for="(err, idx) in periodicValidationErrors"
                  :key="idx"
                  class="experience-editor__periodic-error"
                >{{ err }}</span>
              </div>
            </div>

            <p v-else-if="!periodicEnabled" class="experience-editor__periodic-disabled-hint">
              Enable cycling to set up weekly rounds, daily challenges, or any interval-based content rotation.
            </p>
          </div>

          <!-- A1: Inline rounds canvas — shows the actual structure without opening modals -->
          <div class="experience-editor__rounds-canvas experience-editor__rounds-canvas--spaced">
            <div class="experience-editor__rounds-heading">
              <div>
                <h3>Rounds and questions</h3>
                <p>Questions are grouped under the round names from the quiz or survey configuration.</p>
              </div>
            </div>

            <div v-if="localConfig.rounds.length === 0" class="experience-editor__empty experience-editor__empty--flush">
              <h2>Start shaping the experience</h2>
              <p>Add a round below, then add questions through the Questions button above or via each round card.</p>
            </div>
            <div
              v-for="(round, roundIndex) in localConfig.rounds"
              :key="round.id"
              class="experience-editor__round-card"
              :class="{ 'experience-editor__round-card--expanded': isRoundExpanded(round.id) }"
            >
              <div class="experience-editor__round-summary">
                <button
                  type="button"
                  class="experience-editor__round-toggle"
                  :aria-expanded="isRoundExpanded(round.id)"
                  @click="toggleRoundExpanded(round.id)"
                >
                  <span class="experience-editor__round-chevron" aria-hidden="true">
                    {{ isRoundExpanded(round.id) ? '-' : '+' }}
                  </span>
                  <span class="experience-editor__round-summary-copy">
                    <span class="experience-editor__round-summary-title">{{ roundHeading(round, roundIndex) }}</span>
                    <span class="experience-editor__round-summary-meta">
                      {{ allQuestionsForRound(round).length }} question{{ allQuestionsForRound(round).length !== 1 ? 's' : '' }}
                    </span>
                  </span>
                </button>
                <button
                  v-if="!isPublishedRecord"
                  type="button"
                  class="experience-editor__round-action"
                  :title="`Remove round ${roundIndex + 1}`"
                  @click="removeRound(roundIndex)"
                >
                  Remove
                </button>
              </div>

              <div v-if="isRoundExpanded(round.id)" class="experience-editor__round-body">
                <div class="experience-editor__round-header">
                  <label class="experience-editor__round-label-field">
                    <span class="experience-editor__row-label">Round name</span>
                    <input
                      v-model="round.label"
                      class="experience-editor__round-label-input"
                      type="text"
                      :placeholder="`Round ${roundIndex + 1}`"
                      :disabled="isPublishedRecord"
                    />
                  </label>
                </div>

                <!-- A2: per-round window + max attempts (explicit-rounds mode only).
                     Backing fields live in periodicSchedule.rounds[i].{startDate,endDate,maxAttempts}.
                     Surface here per Luke's "inside this experience questions round editor" ask. -->
                <div
                  v-if="periodicEnabled && periodicMode === 'explicit-rounds'"
                  class="experience-editor__round-window"
                >
                  <div class="experience-editor__round-window-fields">
                    <label class="experience-editor__round-window-field">
                      <span class="experience-editor__row-label">Round start (UTC)</span>
                      <input
                        type="datetime-local"
                        class="experience-editor__round-window-input"
                        :value="getRoundStartLocal(round.id)"
                        :disabled="isPublishedRecord"
                        @change="updateRoundDate(round.id, 'startDate', ($event.target as HTMLInputElement).value)"
                      />
                    </label>
                    <label class="experience-editor__round-window-field">
                      <span class="experience-editor__row-label">Round end (UTC)</span>
                      <input
                        type="datetime-local"
                        class="experience-editor__round-window-input"
                        :value="getRoundEndLocal(round.id)"
                        :disabled="isPublishedRecord"
                        @change="updateRoundDate(round.id, 'endDate', ($event.target as HTMLInputElement).value)"
                      />
                    </label>
                    <label class="experience-editor__round-window-field experience-editor__round-window-field--narrow">
                      <span class="experience-editor__row-label">Max attempts / user</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="∞"
                        class="experience-editor__round-window-input"
                        :value="getRoundMaxAttempts(round.id)"
                        :disabled="isPublishedRecord"
                        @input="updateRoundMaxAttempts(round.id, ($event.target as HTMLInputElement).value)"
                      />
                    </label>
                  </div>
                </div>

                <!-- Questions in this round (truncated text, click to open QuizSection questions modal) -->
                <div v-if="allQuestionsForRound(round).length > 0" class="experience-editor__question-list">
                  <div
                    v-for="(question, questionIndex) in allQuestionsForRound(round)"
                    :key="question.id"
                    class="experience-editor__question-field"
                  >
                    <span class="experience-editor__row-label">
                      Question {{ questionIndex + 1 }}
                    </span>
                    <div
                      class="experience-editor__question-row"
                      :title="question.text"
                    >
                      <button
                        type="button"
                        class="experience-editor__question-open"
                        @click="openQuestionsModal"
                      >
                        <span class="experience-editor__question-text">
                          {{ question.text.length > 80 ? question.text.slice(0, 79) + '…' : question.text }}
                        </span>
                        <span v-if="question.promptFormat && question.promptFormat !== 'text'" class="experience-editor__question-badge">
                          {{ question.promptFormat }}
                        </span>
                        <span class="experience-editor__question-badge">
                          {{ questionTypeLabel(question.type) }}
                        </span>
                      </button>
                      <button
                        v-if="!isPublishedRecord"
                        type="button"
                        class="experience-editor__round-action experience-editor__question-remove"
                        :title="`Remove question ${questionIndex + 1}`"
                        @click="removeQuestion(roundIndex, question.id)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
                <p v-else class="experience-editor__round-empty">No questions yet in this round.</p>

                <button
                  v-if="!isPublishedRecord"
                  type="button"
                  class="experience-editor__round-action experience-editor__round-action--add"
                  @click="openQuestionsModal"
                >
                  + Add question
                </button>
              </div>
            </div>

            <div v-if="!isPublishedRecord" class="experience-editor__canvas-add-round">
              <button
                type="button"
                class="experience-editor__canvas-add-round-btn"
                @click="addRound"
              >
                + Add round
              </button>
            </div>
          </div>
        </section>

        <div class="experience-editor__meta-card experience-editor__workflow-card experience-editor__workflow-card--footer">
          <span class="experience-editor__meta-label">Next actions</span>
          <div class="experience-editor__workflow-actions experience-editor__workflow-actions--footer">
            <button
              type="button"
              class="experience-editor__save-cta"
              :disabled="isSaving"
              @click="() => saveExperience()"
            >
              {{ primaryActionLabel }}
            </button>
            <button
              type="button"
              class="experience-editor__save-cta"
              :disabled="isSaving || localConfig.experienceDefinitionStatus === 'published'"
              :title="localConfig.experienceDefinitionStatus === 'published' ? 'Already published' : 'Save and publish this experience'"
              @click="() => saveExperience('published')"
            >
              {{ localConfig.experienceDefinitionStatus === 'published' ? 'Published' : 'Publish' }}
            </button>
            <router-link
              class="experience-editor__attach-btn"
              :class="{ 'experience-editor__attach-btn--disabled': localConfig.experienceDefinitionStatus !== 'published' }"
              :to="localConfig.experienceDefinitionStatus === 'published' ? { path: '/dashboard', query: { attachExperience: localConfig.experienceDefinitionLocalKey } } : ''"
              :tabindex="localConfig.experienceDefinitionStatus !== 'published' ? -1 : 0"
              :aria-disabled="localConfig.experienceDefinitionStatus !== 'published'"
              :title="localConfig.experienceDefinitionStatus !== 'published' ? 'Publish first to attach this experience to a campaign' : 'Browse campaigns to attach this experience'"
            >
              Attach to campaign
            </router-link>
            <button
              type="button"
              class="experience-editor__archive-btn"
              :disabled="isSaving"
              @click="archiveExperience"
            >
              Archive
            </button>
          </div>
          <div class="experience-editor__attached-summary">
            <span>{{ attachedCampaigns.length }} attached campaign{{ attachedCampaigns.length === 1 ? '' : 's' }}</span>
            <div v-if="attachedCampaigns.length > 0" class="experience-editor__campaign-chips">
              <router-link
                v-for="campaign in attachedCampaigns"
                :key="campaign.campaignId"
                class="experience-editor__campaign-chip"
                :to="{ path: `/dashboard/campaigns/${campaign.campaignId}/edit` }"
              >
                {{ campaign.campaignName }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- Data & Insights section -->
        <section class="experience-editor__data-insights">
          <header
            class="experience-editor__data-insights-header"
            role="button"
            tabindex="0"
            @click="toggleDataInsights"
            @keydown.enter="toggleDataInsights"
            @keydown.space.prevent="toggleDataInsights"
          >
            <div class="experience-editor__data-insights-title-row">
              <h2>Data &amp; Insights</h2>
              <span v-if="exportTotals" class="experience-editor__data-insights-count">
                {{ exportTotals.uniqueUsers }} respondent{{ exportTotals.uniqueUsers === 1 ? '' : 's' }}
              </span>
            </div>
            <span class="experience-editor__data-insights-chevron" aria-hidden="true">
              {{ dataInsightsOpen ? '−' : '+' }}
            </span>
          </header>

          <div v-if="dataInsightsOpen" class="experience-editor__data-insights-body">
            <p class="experience-editor__data-insights-desc">
              Aggregated across all campaigns using this experience.
            </p>

            <div class="experience-editor__data-insights-actions">
              <button
                type="button"
                class="experience-editor__data-insights-btn"
                :disabled="loadingExport || !exportLineageId"
                @click="downloadCsv"
              >
                {{ loadingExport ? 'Fetching…' : 'Download CSV' }}
              </button>
              <button
                type="button"
                class="experience-editor__data-insights-btn experience-editor__data-insights-btn--primary"
                :disabled="loadingExport || !exportLineageId"
                @click="openVisualizer"
              >
                Visualize data
              </button>
            </div>

            <p v-if="exportError" class="experience-editor__data-insights-error">{{ exportError }}</p>
          </div>
        </section>

      </div>
    </section>
  </div>

  <ExperienceDataVisualizer
    v-if="visualizerOpen && exportData"
    :data="exportData"
    @close="closeVisualizer"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { experiencesService } from '@/services/experiences'
import { useAuthStore } from '@/store/auth.store'
import ExperienceDataVisualizer from '@/components/ExperienceDataVisualizer.vue'
import { useExperienceDataExport } from '@/composables/useExperienceDataExport'
import QuizSection from '@/views/form/sections/QuizSection.vue'
import type { ComponentPublicInstance } from 'vue'
import type { QuizConfig, QuizRound, PeriodicSchedule } from '@/types/form.types'
import {
  buildQuizConfigFromTemplate,
  getDefaultTemplateIdForExperienceMode,
} from '@/config/quiz-templates.config'
import {
  archiveExperienceLibraryRecord,
  buildExperienceLibraryRecord,
  duplicateExperienceLibraryRecord,
  getExperienceLibraryRecord,
  listExperienceLibraryRecords,
  materializeExperienceIdentity,
  saveExperienceLibraryRecord,
} from '@/utils/experienceLibrary'

type PeriodicIntervalUnit = 'hour' | 'day' | 'week' | 'month'

const INTERVAL_SECONDS: Record<PeriodicIntervalUnit, number> = {
  hour: 3600,
  day: 86400,
  week: 604800,
  month: 2592000,
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orgStamp = () => ({
  orgId: authStore.activeOrgId || undefined,
  tenantId: authStore.activeTenantId || undefined,
})
const libraryRecords = ref(listExperienceLibraryRecords({ includeArchived: true }))
const quizSectionRef = ref<ComponentPublicInstance & { openQuestions?: () => void } | null>(null)

const newExperienceModes = [
  {
    id: 'quiz',
    label: 'Quiz',
    description: 'Scored questions with right and wrong answers.',
  },
  {
    id: 'survey',
    label: 'Survey',
    description: 'Feedback, ratings, and free-text responses.',
  },
] as const

function buildDefaultConfig(mode: 'quiz' | 'survey' = 'quiz'): QuizConfig {
  const templateId = getDefaultTemplateIdForExperienceMode(mode)
  const templateConfig = buildQuizConfigFromTemplate(templateId, mode)
  if (templateConfig) {
    return materializeExperienceIdentity({
      ...templateConfig,
      experienceTemplateFamilyId: templateId,
      experienceDefinitionName: mode === 'survey' ? 'Survey Experience' : 'Quiz Experience',
      experienceDefinitionStatus: 'draft',
    })
  }

  return materializeExperienceIdentity({
    experienceMode: mode,
    quizTemplateId: templateId,
    experienceTemplateFamilyId: templateId,
    experienceDefinitionName: mode === 'survey' ? 'Survey Experience' : 'Quiz Experience',
    experienceDefinitionStatus: 'draft',
    timeLimitSeconds: 60,
    passThreshold: 3,
    attemptLimit: 3,
    attemptWindow: 'day',
    rounds: [{
      id: 'round-1',
      label: 'Round 1',
      stages: [{ id: 'round-1-stage-1', order: 1, type: mode, questions: [] }],
    }],
    slots: [{
      id: 'slot-round-1',
      label: 'Round 1',
      roundId: 'round-1',
      releaseType: 'immediate',
    }],
    noActiveSlotAction: null,
    leaderboard: {
      enabled: false,
      eventName: 'quiz.completed.score',
      displayMode: 'top-10-plus-user',
      title: 'Top Players',
      emptyStateText: 'Be the first to post a score.',
      showAfterCompletionOnly: true,
    },
    primaryColor: '#9B6DFF',
    backgroundColor: '#050614',
    correctColor: '#39FF14',
    incorrectColor: '#FF3131',
  })
}

function refreshLibraryRecords() {
  libraryRecords.value = listExperienceLibraryRecords({ includeArchived: true })
}

const existingRecord = computed(() => {
  const experienceKey = typeof route.params.experienceKey === 'string' ? route.params.experienceKey : ''
  return experienceKey ? libraryRecords.value.find((item) => item.localKey === experienceKey) || getExperienceLibraryRecord(experienceKey) : null
})

const localConfig = reactive<QuizConfig>(
  materializeExperienceIdentity(existingRecord.value?.quizConfig || buildDefaultConfig()),
)
const saveFeedback = ref('Not saved yet')
const editorMessage = ref<{ kind: 'success' | 'error'; text: string } | null>(null)
const isSaving = ref(false)
const pendingAction = ref<'save' | 'draft' | 'published' | 'fork' | null>(null)
const expandedRoundIds = ref<string[]>([])

watch(
  () => route.params.experienceKey,
  async () => {
    const routeKey = typeof route.params.experienceKey === 'string' ? route.params.experienceKey : ''
    if (routeKey && !getExperienceLibraryRecord(routeKey)) {
      await experiencesService.getExperience(routeKey)
    }
    refreshLibraryRecords()
    const nextConfig = materializeExperienceIdentity(existingRecord.value?.quizConfig || buildDefaultConfig())
    Object.assign(localConfig, nextConfig)
    expandedRoundIds.value = nextConfig.rounds[0]?.id ? [nextConfig.rounds[0].id] : []
    saveFeedback.value = existingRecord.value ? 'Loaded from library' : 'Not saved yet'
  },
  { immediate: true },
)

const campaignSchedule = {
  startDateTime: null,
  endDateTime: null,
  timezone: 'UTC',
}

const experienceName = computed({
  get: () => localConfig.experienceDefinitionName || '',
  set: (value: string) => {
    localConfig.experienceDefinitionName = value
  },
})

const updatedLabel = computed(() => {
  if (!localConfig.experienceDefinitionLastSavedAt) return 'Not saved yet'
  const parsed = new Date(localConfig.experienceDefinitionLastSavedAt)
  if (Number.isNaN(parsed.getTime())) return localConfig.experienceDefinitionLastSavedAt
  return parsed.toLocaleString()
})

const title = computed(() => localConfig.experienceDefinitionName || 'New Experience')
const isPublishedRecord = computed(() => existingRecord.value?.status === 'published')
const isNewExperience = computed(() => !existingRecord.value && route.params.experienceKey === 'new')

const lockedExperienceType = computed(() =>
  localConfig.experienceMode === 'survey' ? 'survey' : localConfig.experienceMode === 'quiz' ? 'quiz' : null,
)

const roundsLabel = computed(() => `${localConfig.rounds.length} round${localConfig.rounds.length !== 1 ? 's' : ''}`)
const stagesLabel = computed(() => {
  const stages = localConfig.rounds.reduce((sum, round) => sum + round.stages.length, 0)
  return `${stages} stage${stages !== 1 ? 's' : ''}`
})
const questionsLabel = computed(() => {
  const questions = localConfig.rounds.reduce((sum, round) => sum + round.stages.reduce((stageSum, stage) => stageSum + stage.questions.length, 0), 0)
  return `${questions} question${questions !== 1 ? 's' : ''}`
})
const questionCount = computed(() =>
  localConfig.rounds.reduce((sum, round) => sum + round.stages.reduce((stageSum, stage) => stageSum + stage.questions.length, 0), 0),
)
const hasNoQuestions = computed(() => questionCount.value === 0)
const familyId = computed(() =>
  existingRecord.value?.familyId
  || existingRecord.value?.lineageId
  || existingRecord.value?.quizConfig.experienceDefinitionId
  || localConfig.experienceDefinitionId
  || localConfig.experienceDefinitionLocalKey
  || '',
)
const versionHistory = computed(() => {
  if (!familyId.value) return []
  return libraryRecords.value
    .filter((record) => !record.archivedAt)
    .filter((record) => (record.familyId || record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey) === familyId.value)
    .sort((a, b) => (b.versionNumber || 1) - (a.versionNumber || 1) || new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
})
const latestPublishedVersionNumber = computed(() => {
  const latestPublished = versionHistory.value
    .filter((record) => record.status === 'published')
    .sort((a, b) => (b.versionNumber || 1) - (a.versionNumber || 1) || new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
  return latestPublished?.versionNumber || null
})
const lineageSummary = computed(() => `${versionHistory.value.length} version${versionHistory.value.length === 1 ? '' : 's'}`)
const workflowMessage = computed(() =>
  isPublishedRecord.value
    ? 'This published version is locked. Fork a new draft to continue iterating.'
    : 'Build and refine the reusable experience here, then publish when the structure is ready.',
)
const lifecycleMessage = computed(() =>
  isPublishedRecord.value
    ? 'Published versions are stable references for campaigns. Editing starts from a duplicated draft.'
    : 'Drafts can be edited freely. Publishing marks this snapshot as a reusable version.',
)
const primaryActionLabel = computed(() => pendingAction.value === 'save' ? 'Saving...' : 'Save experience')

const attachedCampaigns = computed(() => existingRecord.value?.usage?.campaigns || [])

function selectNewExperienceMode(mode: 'quiz' | 'survey') {
  if (!isNewExperience.value || isSaving.value) return
  const nextConfig = buildDefaultConfig(mode)
  Object.assign(localConfig, nextConfig)
  saveFeedback.value = 'Not saved yet'
  editorMessage.value = null
}

function setEditorMessage(kind: 'success' | 'error', text: string) {
  editorMessage.value = { kind, text }
}

function historyUsageLabel(record: typeof versionHistory.value[number]) {
  const count = record.usage?.exactVersionCount || 0
  if (record.status !== 'published') return 'Draft version'
  if (count === 0) return 'Not attached to campaigns'
  if (count === 1) return 'Pinned by 1 campaign'
  return `Pinned by ${count} campaigns`
}

function isLatestPublishedRecord(record: typeof versionHistory.value[number]) {
  return record.status === 'published' && record.versionNumber === latestPublishedVersionNumber.value
}

async function saveExperience(nextStatus?: 'draft' | 'published') {
  if (isPublishedRecord.value) {
    saveFeedback.value = 'Published versions are read-only'
    setEditorMessage('error', 'Published versions are read-only.')
    return
  }
  const previousStatus = localConfig.experienceDefinitionStatus
  const requestedStatus = nextStatus || localConfig.experienceDefinitionStatus || 'draft'
  const lastSavedAt = new Date().toISOString()
  editorMessage.value = null
  isSaving.value = true
  pendingAction.value = nextStatus || 'save'
  const requestConfig = materializeExperienceIdentity({
    ...JSON.parse(JSON.stringify(localConfig)) as QuizConfig,
    experienceDefinitionStatus: requestedStatus,
    experienceDefinitionLastSavedAt: lastSavedAt,
  })

  if (requestedStatus === 'draft') {
    Object.assign(localConfig, requestConfig)
  }

  const record = requestedStatus === 'draft'
    ? saveExperienceLibraryRecord({
        quizConfig: requestConfig,
        campaignName: existingRecord.value?.campaignName,
        ...orgStamp(),
      })
    : buildExperienceLibraryRecord({
        quizConfig: requestConfig,
        campaignName: existingRecord.value?.campaignName,
        ...orgStamp(),
        seed: {
          ...existingRecord.value,
          ...buildExperienceLibraryRecord({
            quizConfig: requestConfig,
            campaignName: existingRecord.value?.campaignName,
            ...orgStamp(),
          }),
          status: 'published',
          recordType: 'published-version',
        },
      })
  try {
    if (record) {
      const saved = await experiencesService.saveExperience(record, { optimisticLocal: requestedStatus === 'draft' })
      await experiencesService.syncLibrary(true)
      refreshLibraryRecords()
      localConfig.experienceDefinitionLocalKey = saved.localKey
      localConfig.experienceDefinitionId = saved.familyId || saved.lineageId || saved.localKey
      localConfig.experienceDefinitionName = saved.name
      localConfig.experienceDefinitionVersionId = `v${saved.versionNumber || 1}`
      localConfig.experienceDefinitionStatus = saved.status
      localConfig.experienceDefinitionLastSavedAt = saved.updatedAt
      saveFeedback.value = saved.status === 'published'
        ? 'Published'
        : 'Saved as draft'
      setEditorMessage(
        'success',
        saved.status === 'published'
          ? `Published as v${saved.versionNumber || 1}.`
          : 'Draft saved.',
      )
    } else {
      saveFeedback.value = 'Save blocked by experience lifecycle rules'
      setEditorMessage('error', 'Save blocked by experience lifecycle rules.')
    }

    const routeKey = typeof route.params.experienceKey === 'string' ? route.params.experienceKey : ''
    const savedKey = localConfig.experienceDefinitionLocalKey || ''
    if (savedKey && routeKey !== savedKey) {
      router.replace({ path: `/dashboard/experiences/${savedKey}` })
    }
  } catch (error: any) {
    localConfig.experienceDefinitionStatus = previousStatus
    if (requestedStatus === 'draft') {
      refreshLibraryRecords()
    }
    const message = error?.response?.data?.error || 'Save blocked by experience lifecycle rules'
    saveFeedback.value = message
    setEditorMessage('error', message)
  } finally {
    isSaving.value = false
    pendingAction.value = null
  }
}

function applyLocalConfigUpdate(nextValue: QuizConfig) {
  Object.assign(localConfig, materializeExperienceIdentity(nextValue))
}

async function editAsDraft() {
  const localKey = localConfig.experienceDefinitionLocalKey
  if (!localKey) return
  editorMessage.value = null
  isSaving.value = true
  pendingAction.value = 'fork'
  const duplicated = duplicateExperienceLibraryRecord(localKey)
  if (!duplicated) {
    isSaving.value = false
    pendingAction.value = null
    setEditorMessage('error', 'Could not create a draft copy.')
    return
  }
  try {
    await experiencesService.saveExperience(duplicated)
    await experiencesService.syncLibrary(true)
    refreshLibraryRecords()
    saveFeedback.value = 'Draft copy created'
    setEditorMessage('success', 'Draft copy created.')
    router.push({ path: `/dashboard/experiences/${duplicated.localKey}` })
  } catch (error: any) {
    const message = error?.response?.data?.error || 'Could not create a draft copy.'
    saveFeedback.value = message
    setEditorMessage('error', message)
  } finally {
    isSaving.value = false
    pendingAction.value = null
  }
}

async function archiveExperience() {
  if (!localConfig.experienceDefinitionLocalKey) return
  const shouldArchive = typeof window === 'undefined'
    ? true
    : window.confirm(`Archive "${localConfig.experienceDefinitionName || 'this experience'}"? You can restore it later from the library.`)
  if (!shouldArchive) return
  archiveExperienceLibraryRecord(localConfig.experienceDefinitionLocalKey)
  await experiencesService.setArchived(localConfig.experienceDefinitionLocalKey, true)
  router.push({ path: '/dashboard/experiences' })
}

function formatDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

// ─── A1: Inline rounds canvas helpers ────────────────────────────────────────

/** All questions flattened across all stages in a round */
function allQuestionsForRound(round: QuizRound) {
  return round.stages.flatMap((stage) => stage.questions)
}

function roundHeading(round: QuizRound, index: number): string {
  const fallback = `Round ${index + 1}`
  const label = round.label?.trim() || fallback
  return label.toLowerCase() === fallback.toLowerCase() ? fallback : `${fallback}: ${label}`
}

function isRoundExpanded(roundId: string): boolean {
  return expandedRoundIds.value.includes(roundId)
}

function toggleRoundExpanded(roundId: string) {
  expandedRoundIds.value = isRoundExpanded(roundId)
    ? expandedRoundIds.value.filter((id) => id !== roundId)
    : [...expandedRoundIds.value, roundId]
}

function questionTypeLabel(type?: string): string {
  switch (type) {
    case 'single-select': return 'single'
    case 'multi-select': return 'multi'
    case 'free-text': return 'text'
    case 'rating': return 'rating'
    case 'boolean': return 'yes/no'
    case 'quiz': return 'quiz'
    default: return type || 'single'
  }
}

function openQuestionsModal() {
  quizSectionRef.value?.openQuestions?.()
}

function generateRoundId(): string {
  return `round-${Math.random().toString(36).slice(2, 9)}`
}

function addRound() {
  const newId = generateRoundId()
  const newRound: QuizRound = {
    id: newId,
    label: `Round ${localConfig.rounds.length + 1}`,
    stages: [{ id: `${newId}-stage-1`, order: 1, type: localConfig.experienceMode === 'survey' ? 'survey' : 'quiz', questions: [] }],
  }
  localConfig.rounds = [...localConfig.rounds, newRound]
  expandedRoundIds.value = [...expandedRoundIds.value.filter((id) => id !== newId), newId]
  emitRoundsUpdate()
}

function removeRound(index: number) {
  const label = localConfig.rounds[index]?.label || `Round ${index + 1}`
  if (typeof window !== 'undefined' && !window.confirm(`Remove "${label}"? Questions in this round will be lost.`)) return
  const removedId = localConfig.rounds[index]?.id
  const next = localConfig.rounds.filter((_, i) => i !== index)
  localConfig.rounds = next
  if (removedId) {
    expandedRoundIds.value = expandedRoundIds.value.filter((id) => id !== removedId)
  }
  emitRoundsUpdate()
}

function removeQuestion(roundIndex: number, questionId: string) {
  const round = localConfig.rounds[roundIndex]
  if (!round) return

  const question = allQuestionsForRound(round).find((item) => item.id === questionId)
  const questionLabel = question?.text?.trim() || 'this question'
  if (typeof window !== 'undefined' && !window.confirm(`Remove "${questionLabel}"?`)) return

  localConfig.rounds = localConfig.rounds.map((item, index) => {
    if (index !== roundIndex) return item

    return {
      ...item,
      stages: item.stages.map((stage) => ({
        ...stage,
        questions: stage.questions.filter((stageQuestion) => stageQuestion.id !== questionId),
      })),
    }
  })
  emitRoundsUpdate()
}

function emitRoundsUpdate() {
  applyLocalConfigUpdate({ ...JSON.parse(JSON.stringify(localConfig)) as QuizConfig })
}

// ─── A2: Periodic schedule helpers ───────────────────────────────────────────

const periodicEnabled = computed(() => Boolean(localConfig.periodicSchedule))

function buildDefaultPeriodicSchedule(): PeriodicSchedule {
  const todayMidnight = new Date()
  todayMidnight.setUTCHours(0, 0, 0, 0)
  return {
    startDate: todayMidnight.toISOString(),
    intervalDurationSeconds: INTERVAL_SECONDS.week,
    intervalNoun: 'week',
    cycle: true,
    killSwitch: false,
    completionMessage: 'Come back next week.',
    rounds: localConfig.rounds.map((round) => ({
      id: round.id,
      label: round.label,
      questionIds: allQuestionsForRound(round).map((q) => q.id),
    })),
  }
}

function togglePeriodic() {
  if (localConfig.periodicSchedule) {
    localConfig.periodicSchedule = null
  } else {
    localConfig.periodicSchedule = buildDefaultPeriodicSchedule()
  }
}

type PeriodicMode = 'uniform-interval' | 'explicit-rounds'

const periodicMode = computed<PeriodicMode>(
  () => (localConfig.periodicSchedule?.scheduleType as PeriodicMode | undefined) ?? 'uniform-interval',
)

function setPeriodicMode(mode: PeriodicMode) {
  if (!localConfig.periodicSchedule) return
  const ps = localConfig.periodicSchedule
  if (mode === 'explicit-rounds') {
    const start = new Date(ps.startDate)
    const intervalMs = (ps.intervalDurationSeconds || INTERVAL_SECONDS.week) * 1000
    const baseTime = Number.isNaN(start.getTime()) ? Date.now() : start.getTime()
    const rounds = ps.rounds.map((r, idx) => ({
      ...r,
      startDate: r.startDate || new Date(baseTime + idx * intervalMs).toISOString(),
      endDate: r.endDate || new Date(baseTime + (idx + 1) * intervalMs).toISOString(),
    }))
    localConfig.periodicSchedule = { ...ps, scheduleType: 'explicit-rounds', rounds }
  } else {
    localConfig.periodicSchedule = { ...ps, scheduleType: 'uniform-interval' }
  }
}

function isoToDateTimeLocal(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 16)
}

function dateTimeLocalToIso(local: string): string {
  if (!local) return ''
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

function getRoundStartLocal(roundId: string): string {
  const r = localConfig.periodicSchedule?.rounds.find((r) => r.id === roundId)
  return isoToDateTimeLocal(r?.startDate)
}

function getRoundEndLocal(roundId: string): string {
  const r = localConfig.periodicSchedule?.rounds.find((r) => r.id === roundId)
  return isoToDateTimeLocal(r?.endDate)
}

function updateRoundDate(roundId: string, field: 'startDate' | 'endDate', localValue: string) {
  if (!localConfig.periodicSchedule) return
  const iso = dateTimeLocalToIso(localValue)
  const rounds = localConfig.periodicSchedule.rounds.map((r) =>
    r.id === roundId ? { ...r, [field]: iso } : r,
  )
  localConfig.periodicSchedule = { ...localConfig.periodicSchedule, rounds }
}

function getRoundMaxAttempts(roundId: string): number | '' {
  const r = localConfig.periodicSchedule?.rounds.find((r) => r.id === roundId)
  return typeof r?.maxAttempts === 'number' && r.maxAttempts > 0 ? r.maxAttempts : ''
}

function updateRoundMaxAttempts(roundId: string, raw: string) {
  if (!localConfig.periodicSchedule) return
  const trimmed = raw.trim()
  let next: number | undefined
  if (trimmed !== '') {
    const parsed = parseInt(trimmed, 10)
    next = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  }
  const rounds = localConfig.periodicSchedule.rounds.map((r) =>
    r.id === roundId ? { ...r, maxAttempts: next } : r,
  )
  localConfig.periodicSchedule = { ...localConfig.periodicSchedule, rounds }
}

/** Convert stored ISO string to a datetime-local value for the input */
const periodicStartLocal = computed(() => {
  const iso = localConfig.periodicSchedule?.startDate
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // datetime-local expects "YYYY-MM-DDTHH:mm"
  return d.toISOString().slice(0, 16)
})

function updatePeriodicStart(localValue: string) {
  if (!localConfig.periodicSchedule) return
  const d = new Date(localValue)
  if (!Number.isNaN(d.getTime())) {
    localConfig.periodicSchedule = { ...localConfig.periodicSchedule, startDate: d.toISOString() }
  }
}

/** Derive the interval unit from stored seconds (best match) */
const periodicIntervalUnit = computed<PeriodicIntervalUnit>(() => {
  const secs = localConfig.periodicSchedule?.intervalDurationSeconds ?? INTERVAL_SECONDS.week
  if (secs % INTERVAL_SECONDS.month === 0 && secs >= INTERVAL_SECONDS.month) return 'month'
  if (secs % INTERVAL_SECONDS.week === 0 && secs >= INTERVAL_SECONDS.week) return 'week'
  if (secs % INTERVAL_SECONDS.day === 0 && secs >= INTERVAL_SECONDS.day) return 'day'
  return 'hour'
})

const periodicIntervalValue = computed<number>(() => {
  const secs = localConfig.periodicSchedule?.intervalDurationSeconds ?? INTERVAL_SECONDS.week
  const unit = periodicIntervalUnit.value
  return Math.round(secs / INTERVAL_SECONDS[unit])
})

function updatePeriodicInterval(value: number, unit: PeriodicIntervalUnit) {
  if (!localConfig.periodicSchedule) return
  const secs = Math.max(1, value || 1) * INTERVAL_SECONDS[unit]
  const noun = unit === 'hour' ? 'hour' : unit === 'day' ? 'day' : unit === 'week' ? 'week' : 'month'
  localConfig.periodicSchedule = {
    ...localConfig.periodicSchedule,
    intervalDurationSeconds: secs,
    intervalNoun: noun,
  }
}

function updatePeriodicField<K extends keyof PeriodicSchedule>(key: K, value: PeriodicSchedule[K]) {
  if (!localConfig.periodicSchedule) return
  localConfig.periodicSchedule = { ...localConfig.periodicSchedule, [key]: value }
}

function periodicRoundQuestionIds(roundId: string): string[] {
  return localConfig.periodicSchedule?.rounds.find((r) => r.id === roundId)?.questionIds ?? []
}

function updatePeriodicRoundQuestionIds(roundId: string, label: string, event: Event) {
  if (!localConfig.periodicSchedule) return
  const select = event.target as HTMLSelectElement
  const selected = Array.from(select.selectedOptions).map((opt) => opt.value)
  const updatedRounds = localConfig.periodicSchedule.rounds.filter((r) => r.id !== roundId)
  updatedRounds.push({ id: roundId, label, questionIds: selected })
  // Preserve original order (matching localConfig.rounds order)
  const orderedIds = localConfig.rounds.map((r) => r.id)
  updatedRounds.sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id))
  localConfig.periodicSchedule = { ...localConfig.periodicSchedule, rounds: updatedRounds }
}

/** Ensure periodicSchedule.rounds stays in sync when quizConfig.rounds changes */
watch(
  () => localConfig.rounds,
  (rounds) => {
    const roundIds = new Set(rounds.map((round) => round.id))
    const retainedExpandedIds = expandedRoundIds.value.filter((id) => roundIds.has(id))
    expandedRoundIds.value = retainedExpandedIds.length > 0
      ? retainedExpandedIds
      : (rounds[0]?.id ? [rounds[0].id] : [])

    if (!localConfig.periodicSchedule) return
    // Add missing round entries, remove stale ones
    const existingMap = new Map(localConfig.periodicSchedule.rounds.map((r) => [r.id, r]))
    const synced = rounds.map((round) => {
      if (existingMap.has(round.id)) return existingMap.get(round.id)!
      return {
        id: round.id,
        label: round.label,
        questionIds: allQuestionsForRound(round).map((q) => q.id),
      }
    })
    localConfig.periodicSchedule = { ...localConfig.periodicSchedule, rounds: synced }
  },
  { deep: true },
)

/** Live preview: "Round N active from <date> to <date>" */
const periodicPreviewLines = computed<string[]>(() => {
  const ps = localConfig.periodicSchedule
  if (!ps || ps.rounds.length === 0) return []
  const lines: string[] = []
  const { rounds } = ps

  if (periodicMode.value === 'explicit-rounds') {
    const showCount = Math.min(rounds.length, 5)
    for (let i = 0; i < showCount; i++) {
      const r = rounds[i]
      const label = r?.label || `Round ${i + 1}`
      if (!r?.startDate || !r?.endDate) {
        lines.push(`${label}: (dates not yet set)`)
        continue
      }
      const s = new Date(r.startDate)
      const e = new Date(r.endDate)
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
        lines.push(`${label}: (invalid date)`)
        continue
      }
      lines.push(`${label}: ${s.toLocaleDateString()} – ${e.toLocaleDateString()}`)
    }
    return lines
  }

  if (!ps.startDate || ps.intervalDurationSeconds <= 0) return []
  const start = new Date(ps.startDate)
  if (Number.isNaN(start.getTime())) return []
  const showCount = Math.min(rounds.length, 5)
  for (let i = 0; i < showCount; i++) {
    const roundStart = new Date(start.getTime() + i * ps.intervalDurationSeconds * 1000)
    const roundEnd = new Date(start.getTime() + (i + 1) * ps.intervalDurationSeconds * 1000)
    const label = rounds[i]?.label || `Round ${i + 1}`
    lines.push(`${label}: ${roundStart.toLocaleDateString()} – ${roundEnd.toLocaleDateString()}`)
  }
  if (ps.cycle && rounds.length > 0) {
    lines.push(`↺ Cycles back to ${rounds[0]?.label || 'Round 1'} after the last round`)
  }
  return lines
})

/** Validation errors for the periodic schedule form */
const periodicValidationErrors = computed<string[]>(() => {
  const ps = localConfig.periodicSchedule
  if (!ps) return []
  const errors: string[] = []
  for (const round of ps.rounds) {
    if (round.questionIds.length === 0) {
      errors.push(`Round "${round.label}" has no questions assigned.`)
    }
  }
  if (periodicMode.value === 'explicit-rounds') {
    const parsed: Array<{ label: string; start: number; end: number }> = []
    for (let i = 0; i < ps.rounds.length; i++) {
      const r = ps.rounds[i]
      const label = r.label || `Round ${i + 1}`
      if (!r.startDate || !r.endDate) {
        errors.push(`${label}: start and end dates are required.`)
        continue
      }
      const s = new Date(r.startDate).getTime()
      const e = new Date(r.endDate).getTime()
      if (Number.isNaN(s) || Number.isNaN(e)) {
        errors.push(`${label}: invalid start or end date.`)
        continue
      }
      if (e <= s) {
        errors.push(`${label}: end must be after start.`)
        continue
      }
      parsed.push({ label, start: s, end: e })
    }
    parsed.sort((a, b) => a.start - b.start)
    for (let i = 1; i < parsed.length; i++) {
      if (parsed[i].start < parsed[i - 1].end) {
        errors.push(`${parsed[i].label} overlaps with ${parsed[i - 1].label}.`)
      }
    }
    return errors
  }

  if (!ps.startDate || Number.isNaN(new Date(ps.startDate).getTime())) {
    errors.push('Start date is required and must be a valid date.')
  }
  if (ps.intervalDurationSeconds <= 0) {
    errors.push('Interval must be greater than 0 seconds.')
  }
  return errors
})

// ─── Data & Insights section ──────────────────────────────────────────────────

const dataInsightsOpen = ref(false)

const exportLineageId = computed(
  () =>
    existingRecord.value?.lineageId ||
    existingRecord.value?.familyId ||
    existingRecord.value?.quizConfig?.experienceDefinitionId ||
    localConfig.experienceDefinitionId ||
    localConfig.experienceDefinitionLocalKey ||
    null,
)

const {
  exportData,
  loading: loadingExport,
  error: exportError,
  visualizerOpen,
  downloadCsv: _downloadCsv,
  openVisualizer: _openVisualizer,
  closeVisualizer,
} = useExperienceDataExport()

const exportTotals = computed(() => exportData.value?.totals ?? null)

function toggleDataInsights() {
  dataInsightsOpen.value = !dataInsightsOpen.value
}

async function downloadCsv() {
  const lineageId = exportLineageId.value
  if (!lineageId) {
    exportError.value = 'No lineageId available — save the experience first.'
    return
  }
  await _downloadCsv(lineageId)
}

async function openVisualizer() {
  const lineageId = exportLineageId.value
  if (!lineageId) {
    exportError.value = 'No lineageId available — save the experience first.'
    return
  }
  await _openVisualizer(lineageId)
}
</script>

<style scoped>
.experience-editor {
  min-height: 100vh;
  max-width: 88rem;
  padding: 1.5rem;
  margin: 6.25rem auto 3.125rem;
  background-color: var(--ui-inverse-surface);
  color: #fff;
  border-radius: 1rem;
  box-shadow:
    0 0.625rem 1.875rem rgba(0, 0, 0, 0.3),
    0 0 0 0.0625rem rgba(var(--vt-c-accent-rgb), 0.1);
  backdrop-filter: blur(0.625rem);
}

.experience-editor__header,
.experience-editor__meta {
  width: 100%;
  margin: 0 auto 1.2rem;
}

.experience-editor__header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.experience-editor__hero {
  display: grid;
  gap: 0.5rem;
}

.experience-editor__header h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(2rem, 4vw, 3.1rem);
}

.experience-editor__title-row {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
}

.experience-editor__family-status {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-editor__family-status--draft {
  background: #facc15;
  color: #422006;
}

.experience-editor__family-status--published {
  background: #22c55e;
  color: #062814;
}

.experience-editor__intro {
  max-width: 42rem;
  margin: 0.8rem 0 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.55;
}

.experience-editor__workflow {
  margin: 0;
  max-width: 44rem;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.5;
}

.experience-editor__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}

.experience-editor__ghost,
.experience-editor__primary,
.experience-editor__danger {
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1rem;
  border-radius: 999px;
  min-height: 2.9rem;
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.experience-editor__ghost:disabled,
.experience-editor__primary:disabled,
.experience-editor__danger:disabled,
.experience-editor__status:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.experience-editor__ghost {
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.32);
  background: var(--ui-inverse-surface);
  color: #fff;
}

.experience-editor__primary {
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-editor__danger {
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-editor__status:disabled,
.experience-editor__input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.experience-editor__meta {
  display: none;
}

.experience-editor__layout {
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
}

.experience-editor__sidebar {
  display: grid;
  gap: 0.9rem;
  position: static;
  order: 2;
}

.experience-editor__family {
  display: grid;
  gap: 1rem;
  padding: 1.1rem;
  border-radius: 1.2rem;
  border: 2px solid var(--vt-c-accent);
  background: rgba(15, 23, 42, 0.42);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__family-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.experience-editor__family-copy {
  display: grid;
  justify-items: start;
  text-align: left;
  flex: 1;
}

.experience-editor__family-copy h3 {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
}

.experience-editor__family-mode {
  margin: 0 0 0.25rem;
  color: rgba(var(--vt-c-accent-rgb), 0.82);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.experience-editor__family-summary {
  margin: 0.45rem 0 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.45;
  font-size: 0.9rem;
}

.experience-editor__family-badges {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  align-items: center;
}

.experience-editor__family-blocks {
  display: grid;
  gap: 1rem;
}

.experience-editor__banner {
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 0.95rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  font-size: 0.92rem;
  line-height: 1.4;
  background: var(--ui-inverse-surface);
}

.experience-editor__banner--success {
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-color: rgba(var(--vt-c-accent-rgb), 0.24);
  color: #cffff0;
}

.experience-editor__banner--error {
  background: rgba(255, 91, 113, 0.08);
  border-color: rgba(255, 91, 113, 0.24);
  color: #ffd7dc;
}

.experience-editor__main {
  display: grid;
  gap: 1rem;
  order: 1;
}

.experience-editor__panel {
  display: grid;
  gap: 0.9rem;
  padding: 1rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__panel--triplet {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.experience-editor__panel--triplet .experience-editor__meta-card {
  min-height: 5.25rem;
  display: grid;
  align-content: center;
  text-align: center;
}

.experience-editor__panel--triplet .experience-editor__meta-label {
  margin-bottom: 0.25rem;
}

.experience-editor__panel--quintet {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.experience-editor__panel--quintet .experience-editor__meta-card {
  min-height: 5.5rem;
  display: grid;
  align-content: center;
  text-align: center;
}

.experience-editor__panel--quintet .experience-editor__meta-label {
  margin-bottom: 0.25rem;
}

.experience-editor__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.2rem;
}

.experience-editor__panel-head h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.experience-editor__meta-card {
  padding: 0.95rem 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.42);
}

.experience-editor__meta-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.experience-editor__meta-value {
  color: #fff;
  font-size: 0.82rem;
  line-height: 1.35;
  word-break: break-word;
}

.experience-editor__history {
  display: grid;
  gap: 0.75rem;
}

.experience-editor__history-item {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1fr);
  gap: 0.35rem;
  padding: 1rem;
  border-radius: 1.1rem;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  color: #fff;
  text-decoration: none;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__history-item--active {
  border-color: var(--vt-c-accent);
  background: rgba(15, 23, 42, 0.88);
}

.experience-editor__history-row {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.experience-editor__history-badges {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  flex-wrap: wrap;
}

.experience-editor__history-version {
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(var(--vt-c-accent-rgb), 0.82);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.experience-editor__history-status {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-editor__history-status--draft {
  background: #facc15;
  color: #422006;
}

.experience-editor__history-status--published {
  background: #22c55e;
  color: #062814;
}

.experience-editor__history-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-editor__history-badge--latest {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-editor__history-name {
  grid-column: 1;
  font-size: 0.98rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.experience-editor__history-date {
  grid-column: 2;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.68);
  display: inline-flex;
  align-items: center;
  justify-self: center;
}

.experience-editor__history-usage {
  grid-column: 3;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.72);
  display: inline-flex;
  align-items: center;
  justify-self: end;
  text-align: right;
}

.experience-editor__input {
  width: 100%;
  padding: 0.72rem 0.95rem;
  border-radius: 0.8rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font: inherit;
}

.experience-editor__status-row {
  display: flex;
  gap: 0.5rem;
}

.experience-editor__type-picker {
  display: grid;
  gap: 0.55rem;
}

.experience-editor__type-pill {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  width: 100%;
  padding: 0.8rem 0.95rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.42);
  color: #fff;
  text-align: left;
  cursor: pointer;
}

.experience-editor__type-pill--active {
  border-color: var(--vt-c-accent);
  background: rgba(var(--vt-c-accent-rgb), 0.12);
}

.experience-editor__type-pill-label {
  font-size: 0.84rem;
  font-weight: 700;
}

.experience-editor__type-pill-desc {
  font-size: 0.74rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.68);
}

.experience-editor__status {
  min-height: 2.4rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
}

.experience-editor__status--active {
  border-color: var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-editor__status--action {
  border-color: var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-editor__status:hover,
.experience-editor__status:active,
.experience-editor__status:focus-visible {
  border-color: var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  outline: none;
}

.experience-editor__readonly {
  padding: 1.2rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.72);
  color: rgba(255, 255, 255, 0.82);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__empty {
  padding: 1.2rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.72);
  color: rgba(255, 255, 255, 0.84);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__empty h2 {
  margin: 0 0 0.4rem;
  color: #fff;
  font-size: 1rem;
}

.experience-editor__empty p {
  margin: 0;
  line-height: 1.5;
}

.experience-editor__readonly h2 {
  margin: 0 0 0.4rem;
  color: #fff;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.experience-editor__readonly p {
  margin: 0;
  line-height: 1.5;
}

.experience-editor__workspace-panel {
  padding: 1.1rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: var(--ui-inverse-surface);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__workspace-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.experience-editor__workspace-head > div:first-child {
  flex: 1;
  display: grid;
  justify-items: start;
  text-align: left;
}

.experience-editor__workspace-head h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.experience-editor__workspace-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.experience-editor__configuration-basics {
  display: grid;
  grid-template-columns: minmax(16rem, 1.4fr) minmax(12rem, 0.8fr);
  gap: 0.75rem;
  margin: 0.75rem 0 1rem;
}

.experience-editor__configuration-basics .experience-editor__meta-card:has(.experience-editor__type-picker) {
  grid-column: 1 / -1;
}

.experience-editor__workflow-card {
  grid-column: 1 / -1;
}

.experience-editor__workflow-actions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  gap: 0.65rem;
}

.experience-editor__workflow-actions--readonly {
  grid-template-columns: minmax(10rem, 14rem) minmax(10rem, 14rem);
  justify-content: center;
  margin-top: 1rem;
}

.experience-editor__workflow-actions--footer {
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.experience-editor__workflow-card--footer {
  margin-top: 1.5rem;
}

.experience-editor__attached-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.78rem;
}

.experience-editor__configuration-basics .experience-editor__type-picker {
  grid-template-columns: repeat(2, minmax(11rem, 14rem));
  justify-content: center;
}

.experience-editor__configuration-basics .experience-editor__type-pill {
  min-height: 4.2rem;
}

.experience-editor__workspace-panel :deep(.quiz-section) {
  min-height: 0;
  padding: 0;
  margin: 0;
  scroll-margin-top: 0;
}

.experience-editor__workspace-panel :deep(.quiz-section > .quiz-shell) {
  flex: 0 0 auto;
}

.experience-editor__workspace-panel :deep(.quiz-shell) {
  flex: 0 0 auto;
}

.experience-editor__workspace-panel :deep(.quiz-content) {
  gap: 0.75rem;
  max-width: 40rem;
}

.experience-editor__workspace-panel :deep(.quiz-template-picker) {
  max-width: 32rem;
}

.experience-editor__workspace-panel :deep(.quiz-cards) {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 0.75rem;
  max-width: 32rem;
}

.experience-editor__workspace-panel :deep(.quiz-card) {
  width: 100%;
  min-width: 0;
  height: 3.85rem;
  min-height: 3.85rem;
  padding: 0.72rem 1rem;
  border-radius: 999px;
  box-shadow: 0 0.7rem 1.4rem rgba(0, 0, 0, 0.16);
  transition: background 150ms ease, border-color 150ms ease, transform 120ms ease, box-shadow 150ms ease;
}

.experience-editor__workspace-panel :deep(.quiz-card:hover) {
  transform: translateY(-1px);
  box-shadow: 0 0.95rem 1.7rem rgba(0, 0, 0, 0.2);
}

.experience-editor__workspace-panel :deep(.quiz-card:focus-visible) {
  outline: 2px solid rgba(var(--vt-c-accent-rgb), 0.55);
  outline-offset: 3px;
}

.experience-editor__workspace-panel :deep(.quiz-card__title) {
  text-align: center;
  line-height: 1.2;
}

@media (max-width: 960px) {
  .experience-editor__header {
    flex-direction: column;
  }

  .experience-editor__workspace-head {
    flex-direction: column;
  }

  .experience-editor__configuration-basics {
    grid-template-columns: 1fr;
  }

  .experience-editor__workflow-actions,
  .experience-editor__workflow-actions--readonly {
    grid-template-columns: 1fr;
  }

  .experience-editor__configuration-basics .experience-editor__type-picker {
    grid-template-columns: 1fr;
  }

  .experience-editor__family-header {
    flex-direction: column;
  }

  .experience-editor__workspace-panel :deep(.quiz-cards) {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .experience-editor__history-item {
    grid-template-columns: 1fr;
  }

  .experience-editor__history-date,
  .experience-editor__history-usage {
    justify-self: start;
    text-align: left;
  }

  .experience-editor__panel--triplet {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .experience-editor__panel--quintet {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .experience-editor {
    padding: 1.1rem;
    margin: 5rem 0.75rem 2rem;
  }

  .experience-editor__actions {
    width: 100%;
  }

  .experience-editor__ghost,
  .experience-editor__primary,
  .experience-editor__danger {
    flex: 1 1 calc(50% - 0.75rem);
  }

  .experience-editor__workspace-panel :deep(.quiz-cards) {
    grid-template-columns: 1fr;
  }

  .experience-editor__panel--triplet {
    grid-template-columns: 1fr;
  }

  .experience-editor__panel--quintet {
    grid-template-columns: 1fr;
  }

}

/* Campaign attachment chips panel */
.experience-editor__panel--campaigns {
  display: grid;
  gap: 0.65rem;
}

.experience-editor__campaign-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.experience-editor__campaign-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(var(--vt-c-accent-rgb), 0.14);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.3);
  color: var(--vt-c-accent);
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  transition: background 150ms ease, border-color 150ms ease;
}

.experience-editor__campaign-chip:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.24);
  border-color: var(--vt-c-accent);
}

.experience-editor__campaign-empty {
  margin: 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  font-style: italic;
}

/* Compact meta line: v{N} · template · shape · save state */
.experience-editor__panel--compact-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0.75rem 1rem;
}

.experience-editor__compact-meta-line {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  line-height: 1.4;
}

.experience-editor__compact-meta-save {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  font-style: italic;
}

/* Inline rounds canvas */
.experience-editor__rounds-canvas {
  display: grid;
  gap: 0.75rem;
}

.experience-editor__rounds-heading {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.15rem;
}

.experience-editor__rounds-heading h3 {
  margin: 0;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.experience-editor__rounds-heading p {
  margin: 0.3rem 0 0;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.82rem;
  line-height: 1.4;
}

.experience-editor__round-card {
  padding: 0.75rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
  background: rgba(15, 23, 42, 0.55);
}

.experience-editor__round-card--expanded {
  border-color: rgba(var(--vt-c-accent-rgb), 0.28);
  background: rgba(15, 23, 42, 0.68);
}

.experience-editor__round-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.6rem;
}

.experience-editor__round-toggle {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  justify-content: stretch;
  gap: 0.65rem;
  width: 100%;
  height: auto;
  min-height: 3.1rem;
  margin-top: 0;
  padding: 0.55rem 0.7rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: rgba(255, 255, 255, 0.035);
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
}

.experience-editor__round-toggle:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-color: rgba(var(--vt-c-accent-rgb), 0.28);
}

.experience-editor__round-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.25);
  color: var(--vt-c-accent);
  font-size: 1rem;
  font-weight: 800;
  line-height: 1;
}

.experience-editor__round-summary-copy {
  display: grid;
  gap: 0.18rem;
  min-width: 0;
}

.experience-editor__round-summary-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  font-weight: 800;
}

.experience-editor__round-summary-meta {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.72rem;
  font-weight: 700;
}

.experience-editor__round-body {
  display: grid;
  gap: 0.75rem;
  margin-top: 0.8rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(var(--vt-c-accent-rgb), 0.14);
}

.experience-editor__round-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}

.experience-editor__round-title-row h4 {
  margin: 0;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.25;
}

.experience-editor__round-header {
  display: grid;
  grid-template-columns: minmax(6.5rem, 8rem) minmax(0, 1fr);
  align-items: center;
  gap: 0.6rem;
}

.experience-editor__round-label-field {
  display: contents;
}

.experience-editor__row-label {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
}

.experience-editor__round-label-input {
  width: 100%;
  padding: 0.3rem 0.55rem;
  border-radius: 0.6rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
}

.experience-editor__round-meta {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}

.experience-editor__round-action {
  flex: 0 0 auto;
  width: auto;
  height: auto;
  min-height: 2rem;
  margin-top: 0;
  padding: 0.25rem 0.55rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.25);
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}

.experience-editor__round-action:hover {
  background: rgba(255, 80, 80, 0.15);
  color: #ff6b6b;
  border-color: rgba(255, 80, 80, 0.35);
}

.experience-editor__round-action--add {
  width: 100%;
  min-height: 3rem;
  margin-top: 0.25rem;
  color: rgba(var(--vt-c-accent-rgb), 0.9);
  border-color: rgba(var(--vt-c-accent-rgb), 0.3);
}

.experience-editor__round-action--add:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  color: var(--vt-c-accent);
  border-color: rgba(var(--vt-c-accent-rgb), 0.5);
}

.experience-editor__question-list {
  display: grid;
  gap: 0.35rem;
}

.experience-editor__question-field {
  display: grid;
  grid-template-columns: minmax(6.5rem, 8rem) minmax(0, 1fr);
  align-items: center;
  gap: 0.5rem;
}

.experience-editor__question-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.experience-editor__question-open {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  justify-content: stretch;
  gap: 0.5rem;
  width: 100%;
  height: auto;
  min-height: 3.15rem;
  margin-top: 0;
  padding: 0.4rem 0.6rem;
  border-radius: 0.65rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.1);
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease, border-color 120ms ease;
}

.experience-editor__question-open:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-color: rgba(var(--vt-c-accent-rgb), 0.25);
}

.experience-editor__question-remove {
  align-self: stretch;
  min-height: 3.15rem;
}

.experience-editor__question-text {
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.82);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.experience-editor__question-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.42rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(var(--vt-c-accent-rgb), 0.12);
  color: var(--vt-c-accent);
  white-space: nowrap;
  flex-shrink: 0;
}

.experience-editor__round-empty {
  margin: 0 0 0.45rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

.experience-editor__canvas-add-round {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.experience-editor__canvas-add-round-btn {
  padding: 0.55rem 1.25rem;
  border-radius: 999px;
  border: 1.5px dashed rgba(var(--vt-c-accent-rgb), 0.3);
  background: transparent;
  color: rgba(var(--vt-c-accent-rgb), 0.7);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
}

.experience-editor__canvas-add-round-btn:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-color: var(--vt-c-accent);
  color: var(--vt-c-accent);
}

/* Periodic schedule panel */
.experience-editor__periodic-panel {
  padding: 1.1rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: var(--ui-inverse-surface);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-editor__periodic-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.experience-editor__periodic-head h2 {
  margin: 0;
  font-size: 1.1rem;
}

.experience-editor__periodic-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.experience-editor__periodic-toggle input[type="checkbox"] {
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
  accent-color: var(--vt-c-accent);
}

.experience-editor__periodic-mode-toggle {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-radius: 0.4rem;
  background: rgba(255, 255, 255, 0.03);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  flex-wrap: wrap;
}

.experience-editor__periodic-mode-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ui-inverse-text-muted);
}

.experience-editor__periodic-mode-options {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.experience-editor__periodic-mode-option {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.55rem;
  border-radius: 0.35rem;
  font-size: 0.82rem;
  cursor: pointer;
  border: 0.0625rem solid transparent;
  transition: border-color 0.15s, background 0.15s;
}

.experience-editor__periodic-mode-option.is-active {
  border-color: var(--vt-c-accent);
  background: rgba(122, 230, 177, 0.06);
}

.experience-editor__periodic-explicit-hint {
  padding: 0.55rem 0.7rem;
  border-radius: 0.4rem;
  background: rgba(122, 230, 177, 0.06);
  border: 0.0625rem dashed rgba(122, 230, 177, 0.3);
}

.experience-editor__periodic-explicit-hint-text {
  font-size: 0.82rem;
  color: var(--ui-inverse-text-muted);
  line-height: 1.4;
}

.experience-editor__round-window {
  margin-top: 0.6rem;
  padding: 0.6rem 0.7rem;
  border-radius: 0.45rem;
  background: rgba(255, 255, 255, 0.03);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.experience-editor__round-window-fields {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.experience-editor__round-window-field {
  flex: 1 1 11rem;
  min-width: 9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.experience-editor__round-window-field--narrow {
  flex: 0 0 9rem;
}

.experience-editor__round-window-input {
  width: 100%;
  padding: 0.35rem 0.55rem;
  border-radius: 0.3rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.14);
  background: rgba(0, 0, 0, 0.25);
  color: var(--ui-inverse-text, #fff);
  font-size: 0.85rem;
}

.experience-editor__round-window-input:disabled {
  opacity: 0.6;
}

.experience-editor__periodic-fields {
  display: grid;
  gap: 0.85rem;
}

.experience-editor__periodic-row {
  display: grid;
  gap: 0.3rem;
}

.experience-editor__periodic-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.2rem;
}

.experience-editor__periodic-input,
.experience-editor__periodic-select,
.experience-editor__periodic-textarea {
  width: 100%;
  padding: 0.55rem 0.85rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font: inherit;
  font-size: 0.88rem;
}

.experience-editor__periodic-textarea {
  min-height: 3.5rem;
  resize: vertical;
}

.experience-editor__periodic-interval-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.experience-editor__periodic-checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 600;
}

.experience-editor__periodic-checkbox-row input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: var(--vt-c-accent);
}

.experience-editor__periodic-rounds-head {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin: 0.2rem 0 0.55rem;
}

.experience-editor__periodic-round-assign {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

.experience-editor__periodic-round-row {
  display: grid;
  gap: 0.25rem;
  padding: 0.65rem 0.85rem;
  border-radius: 0.85rem;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.12);
}

.experience-editor__periodic-round-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.82);
}

.experience-editor__periodic-round-select {
  width: 100%;
  padding: 0.4rem 0.65rem;
  border-radius: 0.6rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font: inherit;
  font-size: 0.82rem;
}

.experience-editor__periodic-preview {
  margin-top: 0.35rem;
  padding: 0.7rem 0.9rem;
  border-radius: 0.85rem;
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
}

.experience-editor__periodic-preview-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(var(--vt-c-accent-rgb), 0.8);
  margin-bottom: 0.4rem;
}

.experience-editor__periodic-preview-lines {
  display: grid;
  gap: 0.2rem;
}

.experience-editor__periodic-preview-line {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
}

.experience-editor__periodic-validation {
  margin-top: 0.35rem;
  display: grid;
  gap: 0.2rem;
}

.experience-editor__periodic-error {
  font-size: 0.75rem;
  color: #ff6b6b;
  font-weight: 600;
}

/* F6: Status toggle — Draft / Published as a compact toggle pair */
.experience-editor__status-toggle {
  display: flex;
  gap: 0.4rem;
}

.experience-editor__status-state {
  flex: 1;
  min-height: 2.2rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 150ms ease, box-shadow 150ms ease;
}

.experience-editor__status-state:not(.experience-editor__status-state--active) {
  opacity: 0.55;
}

.experience-editor__status-state:hover:not(:disabled):not(.experience-editor__status-state--active) {
  opacity: 0.8;
}

.experience-editor__status-state:disabled {
  cursor: not-allowed;
}

.experience-editor__status-state--active {
  box-shadow: 0 0 0 2px rgba(var(--vt-c-accent-rgb), 0.35);
}

/* F6/F7: Workflow action buttons — shared pill base, semantic color variants */
.experience-editor__save-cta,
.experience-editor__archive-btn,
.experience-editor__attach-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.75rem;
  margin-top: 0;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration: none;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, opacity 150ms ease, transform 100ms ease;
}

.experience-editor__save-cta {
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 800;
}

.experience-editor__save-cta:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.experience-editor__attach-btn {
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 800;
}

.experience-editor__attach-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.experience-editor__attach-btn--disabled,
.experience-editor__attach-btn--disabled:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
  border-color: rgba(255, 255, 255, 0.1);
  pointer-events: none;
  cursor: not-allowed;
  transform: none;
  opacity: 1;
}

.experience-editor__archive-btn {
  border-color: rgba(255, 80, 80, 0.4);
  color: rgba(255, 130, 130, 0.85);
}

.experience-editor__archive-btn:hover:not(:disabled) {
  background: rgba(255, 80, 80, 0.12);
  border-color: rgba(255, 80, 80, 0.6);
  color: #ff8080;
}

.experience-editor__save-cta:disabled,
.experience-editor__archive-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* F6: Archive — legacy row wrapper (readonly view) */
.experience-editor__archive-row {
  display: flex;
  justify-content: center;
  padding-top: 0.25rem;
}
.experience-editor__save-cta-row {
  padding: 0 0.25rem;
}

/* Banner: info variant for campaign-snapshot warning */
.experience-editor__banner--info {
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border-color: rgba(var(--vt-c-accent-rgb), 0.22);
  color: rgba(255, 255, 255, 0.85);
}

/* F3: Cycling schedule as section-within-panel — strip outer panel chrome */
.experience-editor__periodic-panel--inline {
  margin-top: 1.1rem;
  padding: 0.85rem 0 0;
  border-radius: 0;
  border: none;
  border-top: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: none;
  box-shadow: none;
}

/* Periodic panel title — replaces inline style */
.experience-editor__periodic-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
}

.experience-editor__periodic-title-optional {
  font-weight: 400;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Spacing helpers — replace inline style= attributes */
.experience-editor__periodic-preview--spaced {
  margin-top: 0.65rem;
}

.experience-editor__periodic-fields--spaced {
  margin-top: 0.85rem;
}

.experience-editor__periodic-row--checkboxes {
  display: grid;
  gap: 0.5rem;
}

.experience-editor__rounds-canvas--spaced {
  margin-top: 1.25rem;
}

.experience-editor__empty--flush {
  margin: 0;
}

/* Disabled cycling hint */
.experience-editor__periodic-disabled-hint {
  margin: 0.65rem 0 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  line-height: 1.4;
}

/* Compact meta line panel — tighter visual register */
.experience-editor__panel--compact-meta {
  padding: 0.6rem 1rem;
  border-color: rgba(var(--vt-c-accent-rgb), 0.1);
  background: rgba(15, 23, 42, 0.25);
  box-shadow: none;
}

/* Attached-campaigns panel — consistent body panel chrome */
.experience-editor__panel--campaigns {
  border-color: rgba(var(--vt-c-accent-rgb), 0.18);
  background: rgba(15, 23, 42, 0.45);
}

.experience-editor__periodic-preview--prominent {
  padding: 0.75rem 1rem;
  border-radius: 0.85rem;
  border-left: 3px solid var(--vt-c-accent);
  background: rgba(var(--vt-c-accent-rgb), 0.06);
}

.experience-editor__periodic-preview--prominent .experience-editor__periodic-preview-title {
  display: block;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--vt-c-accent-rgb), 0.8);
  margin-bottom: 0.4rem;
}

.experience-editor__periodic-preview--prominent .experience-editor__periodic-preview-lines {
  display: grid;
  gap: 0.25rem;
}

.experience-editor__periodic-preview--prominent .experience-editor__periodic-preview-line {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.82);
}

.experience-editor__periodic-preview-empty {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

/* ── Data & Insights section ──────────────────────── */

.experience-editor__data-insights {
  margin-top: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  overflow: hidden;
}

.experience-editor__data-insights-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  user-select: none;
  background: rgba(255, 255, 255, 0.03);
  transition: background 0.15s;
}

.experience-editor__data-insights-header:hover {
  background: rgba(255, 255, 255, 0.06);
}

.experience-editor__data-insights-header:focus-visible {
  outline: 2px solid rgba(var(--vt-c-accent-rgb), 0.6);
  outline-offset: -2px;
}

.experience-editor__data-insights-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.experience-editor__data-insights-title-row h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
}

.experience-editor__data-insights-count {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  padding: 2px 10px;
}

.experience-editor__data-insights-chevron {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 300;
  flex-shrink: 0;
}

.experience-editor__data-insights-body {
  padding: 1rem 1.25rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.experience-editor__data-insights-desc {
  font-size: 0.84rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 1rem;
}

.experience-editor__data-insights-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.experience-editor__data-insights-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 0.87rem;
  font-weight: 500;
  padding: 0.5rem 1.1rem;
  transition: background 0.15s, color 0.15s;
}

.experience-editor__data-insights-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.experience-editor__data-insights-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.experience-editor__data-insights-btn--primary {
  background: rgba(var(--vt-c-accent-rgb), 0.15);
  border-color: rgba(var(--vt-c-accent-rgb), 0.4);
  color: var(--vt-c-accent);
}

.experience-editor__data-insights-btn--primary:hover:not(:disabled) {
  background: rgba(var(--vt-c-accent-rgb), 0.25);
}

.experience-editor__data-insights-error {
  margin: 0.75rem 0 0;
  font-size: 0.82rem;
  color: #ff6b6b;
}
</style>
